'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Timer, Square, Minimize2, Pause, Play, X } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import Link from 'next/link'
import { useToast } from '@/hooks/use-toast'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function FloatingTimer() {
  const activeTimer = useTimeTracking((state) => state.activeTimer)
  const stopTimer = useTimeTracking((state) => state.stopTimer)
  const pauseTimer = useTimeTracking((state) => state.pauseTimer)
  const resumeTimer = useTimeTracking((state) => state.resumeTimer)

  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [isMinimized, setIsMinimized] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [showDiscardAlert, setShowDiscardAlert] = useState(false)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const { toast } = useToast()

  // Wait for hydration
  useEffect(() => {
    setMounted(true)
  }, [])

  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const t = useTranslations('issuesPage')

  // Main timer effect - simplified
  useEffect(() => {
    if (!activeTimer || !mounted) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setElapsedSeconds(0)
      setIsMinimized(false)
      return
    }

    // Calculate elapsed time
    const calculateElapsed = () => {
      if (activeTimer.isPaused) {
        return activeTimer.pausedTime
      }
      const currentElapsed = Math.floor((Date.now() - activeTimer.startTime) / 1000)
      return activeTimer.pausedTime + currentElapsed
    }

    // Update immediately
    setElapsedSeconds(calculateElapsed())

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    // Only run interval if not paused
    if (!activeTimer.isPaused) {
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(calculateElapsed())
      }, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [activeTimer, mounted])

  const formatTime = useCallback((seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }, [])

  const formatTimeShort = useCallback((seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return hours > 0 ? `${hours}:${minutes.toString().padStart(2, '0')}` : `${minutes}m`
  }, [])

  const handleStopTimer = useCallback(() => {
    if (!activeTimer) return

    // Calculate elapsed time
    const elapsed = activeTimer.isPaused
      ? activeTimer.pausedTime
      : activeTimer.pausedTime + Math.floor((Date.now() - activeTimer.startTime) / 1000)

    const hours = (elapsed / 3600).toFixed(2)

    // Open the global time tracker sidebar with pre-filled context
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('openTimeTracking', {
        detail: {
          issueId: activeTimer.issueId,
          projectId: activeTimer.projectId,
          elapsedSeconds: Math.floor(elapsed),
          timeEntry: {
            hours,
            activityId: activeTimer.activityId,
            activityName: activeTimer.activityName,
            comments: activeTimer.description
          }
        }
      }))
    }
  }, [activeTimer])
  
  const handleDiscardTimer = useCallback(() => {
    if (!activeTimer) {
      return
    }
    setShowDiscardAlert(true)
  }, [activeTimer])

  const confirmDiscardTimer = useCallback(() => {
    stopTimer()
    setShowDiscardAlert(false)
    toast({
      title: 'Timer Discarded',
      description: 'The timer has been stopped and time was not logged.',
    })
  }, [stopTimer, toast])
  

  const handlePauseResume = useCallback(() => {
    if (!activeTimer) return

    if (activeTimer.isPaused) {
      resumeTimer()
    } else {
      pauseTimer()
    }
  }, [activeTimer?.isPaused, pauseTimer, resumeTimer])

  const handleMinimize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMinimized(true)
  }, [])

  const handleMaximize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsMinimized(false)
  }, [])

  // Don't render until mounted (hydration complete)
  if (!mounted) return null

  // Don't show timer widget if no active timer
  if (!activeTimer) return null

  // Minimized timer - sleek floating button
  if (isMinimized) {
    return (
      <Button
        data-floating-timer="true"
        onClick={handleMaximize}
        className="fixed bottom-6 right-6 z-[60] h-16 px-6 rounded-2xl shadow-2xl bg-gradient-to-r from-primary via-primary to-primary/90 hover:from-primary/90 hover:via-primary/80 hover:to-primary/70 text-white border-0 transition-all hover:scale-105"
      >
          <div className="flex items-center gap-3">
          <div className="relative">
            <Timer className="h-6 w-6" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium opacity-90">Timer Active</span>
            <span className="text-lg font-bold tabular-nums leading-none">{formatTimeShort(elapsedSeconds)}</span>
          </div>
        </div>
      </Button>
    )
  }

  // Expanded floating timer - modern card
  return (
    <>
      <Card data-floating-timer="true" className="fixed bottom-6 right-6 z-[60] w-[calc(100%-48px)] max-w-[420px] shadow-2xl border-0 bg-card overflow-hidden ring-1 ring-border/50 dark:ring-border/20">
        {/* Header - Clean and minimal */}
        <div className="px-6 py-4 border-b border-border/50 dark:border-border/30 bg-muted/30 dark:bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Timer className="h-5 w-5 text-primary" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-card animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground text-sm">{t('timerRunning')}</h3>
                <p className="text-muted-foreground text-xs">{t('trackYourTime')}</p>
              </div>
            </div>
            <button
              onClick={handleMinimize}
              className="h-8 w-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
            >
              <Minimize2 className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4">
          {/* Issue Info */}
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Link
                  href={`/issues/${activeTimer.issueId}`}
                  className="text-primary hover:underline font-semibold text-sm"
                >
                  #{activeTimer.issueId}
                </Link>
                <span className="text-xs px-2 py-0.5 rounded-md bg-primary/10 text-primary font-medium">
                  {activeTimer.activityName}
                </span>
              </div>
              <p className="text-sm text-foreground line-clamp-2 leading-relaxed">
                {activeTimer.issueSubject}
              </p>
            </div>
          </div>

          {/* Timer Display */}
          <div className="bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 rounded-xl p-4 border-0 ring-1 ring-primary/20 dark:ring-primary/30">
            <div className="flex items-center justify-between">
              <div>
                    <div className="text-sm text-muted-foreground mb-1">
                      {t('elapsedTime')} {activeTimer.isPaused && <span className="text-amber-500">({t('paused')})</span>}
                    </div>
                <div className="text-4xl font-bold text-primary tabular-nums tracking-tight">
                  {formatTime(elapsedSeconds)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">{t('started')}</div>
                <div className="text-sm font-medium text-foreground mt-1">
                  {new Date(activeTimer.startTime).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handlePauseResume}
              variant="outline"
              className="flex-1 h-12 font-semibold rounded-xl"
            >
              {activeTimer.isPaused ? (
                <>
                  <Play className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')} />
                  {t('resume')}
                </>
              ) : (
                <>
                  <Pause className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')} />
                  {t('pause')}
                </>
              )}
            </Button>
            <Button
              onClick={handleStopTimer}
              className="flex-1 h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-xl shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30"
            >
              <Square className={cn('h-5 w-5', isRTL ? 'ml-2' : 'mr-2')} />
              {t('stopAndReview')}
            </Button>
          </div>
          
          {/* Discard Timer Button */}
          <Button
            onClick={handleDiscardTimer}
            variant="ghost"
            className="w-full h-10 text-destructive hover:text-destructive hover:bg-destructive/10 font-medium rounded-xl"
          >
            <X className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
            Discard Timer
          </Button>
        </div>
        </Card>

      {/* Discard Confirmation Dialog */}
      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent className="z-[10000]">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Timer?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to discard this timer? All tracked time will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDiscardTimer}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard Timer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
